import { ChangeEvent, ReactNode, useMemo, useState } from 'react';
import { debounce, DebounceTimeoutValues, Select, Selected, SelectProps } from '@optimus-web/core';

export interface AsyncSelectProps<T> extends Omit<SelectProps, 'children'> {
    name: string;
    useDataQuery: (searchValue: string) => { isFetching: boolean; data?: T };
    renderData: (data: T) => ReactNode;
    defaultSearchValue?: string;
    defaultOptions?: ReactNode;
}

export const AsyncSelect = <T,>({
    onChange,
    useDataQuery,
    renderData,
    defaultSearchValue = '',
    defaultOptions = null,
    ...props
}: AsyncSelectProps<T>) => {
    const [searchValue, setSearchValue] = useState(defaultSearchValue);
    const [selected, setSelected] = useState<Selected | undefined>(props.selected);

    const { isFetching, data } = useDataQuery(searchValue);

    const debouncedSetSearchTerm = useMemo(
        () =>
            debounce((newSearchValue: string) => {
                setSearchValue(newSearchValue);
            }, DebounceTimeoutValues.textInput),
        []
    );

    const handleSearchChange = (newSearchValue: string) => {
        // Immediate items cleanup if no search value
        if (!newSearchValue) {
            return setSearchValue(newSearchValue);
        }

        debouncedSetSearchTerm(newSearchValue);
    };

    const handleChange = (_event: ChangeEvent, selectedValue: Selected) => {
        setSelected(selectedValue);
        onChange?.(_event, selectedValue);
    };

    const asyncSelectDefaultChildren = searchValue.length > 0 && data ? renderData(data) : defaultOptions;

    /**
     * In the Select component, chips are displayed based on the component's children.
     * To ensure a smooth search experience, the default options should always
     * be shown in the case of a multi-select (same as in SelectField).
     */
    return (
        <Select
            {...props}
            onSearchChange={handleSearchChange}
            onChange={handleChange}
            loader={isFetching}
            virtualized
            selected={selected}
        >
            {props.multiselect && defaultOptions}
            {props.multiselect && searchValue.length > 0 && data && renderData(data)}
            {props.multiselect ? null : asyncSelectDefaultChildren}
        </Select>
    );
};
